import { useCallback, useEffect, useRef } from 'react';
import { LocationSelected } from '../../../store/signup/types';
import { useRequestState, requestError, RequestState } from './useRequestState';

interface Coordinates {
    latitude: any;
    longitude: any;
    accuracy: any;
}

export const getLocation = (fn: PositionCallback) => {
    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(fn);
    }
};

const applyFilter = (
    term: string,
    location: LocationSelected,
    coordinates: Coordinates | undefined,
): string => {
    const { city, state, zip, userTypedValue } = location;
    return JSON.stringify({
        term: !city && !zip ? term + ' ' + userTypedValue : term,
        city,
        state,
        zip,
        filter: 'all',
        location: coordinates
            ? `${coordinates.latitude},${coordinates.longitude}`
            : undefined,
        accuracy: coordinates?.accuracy,
    });
};

type ResponseFunctionType<T> = (
    res: MessageEvent,
    fn: React.Dispatch<React.SetStateAction<RequestState<T>>>,
) => unknown;

export const useWebSocket = <Response>(
    url: string,
    onMessage: ResponseFunctionType<Response>,
) => {
    const [requestState, setRequestState] = useRequestState<Response>();
    const ws = useRef<WebSocket | null>(null);

    const openConnection = (term?: string) => {
        ws.current = new WebSocket(url);
        ws.current.onerror = () => {
            setRequestState(
                requestError(new Error('Unable to create WebSocket')),
            );
        };
        ws.current.onopen = () => {
            if (term && ws.current?.readyState === WebSocket.OPEN) {
                ws.current?.send(term);
            }
        };
        ws.current.onmessage = (res) => onMessage(res, setRequestState);
    };

    useEffect(() => {
        if (ws.current?.readyState === WebSocket.OPEN) return;
        openConnection();
        return () => {
            ws.current?.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const makeRequest = useCallback(
        async (
            term: string,
            location: LocationSelected,
            coordinates: Coordinates | undefined,
        ) => {
            const values = applyFilter(term, location, coordinates);
            if (ws.current?.readyState === WebSocket.OPEN) {
                ws.current.send(values);
            } else {
                openConnection(values);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ws.current?.readyState],
    );
    return [requestState, makeRequest] as const;
};
